import { useTheme } from "@/contexts/RADashboardThemeContext/hooks"
import { LayoutEditorProps, RADashboardLayout } from "@/interfaces/raDashboard.interface"
import { Box, TextField } from "@mui/material"
import React from "react"
import DashboardKeyStringsInfo from "./DashboardKeyStringsInfo"

const DashboardTextEditor: React.FC<LayoutEditorProps> = (props) => {
  const theme = useTheme();

  const [text, setText] = React.useState("")
  const [isValid, setIsValid] = React.useState(true)

  //Stringifys with proper indents for display
  const layoutToText = (layout: RADashboardLayout | undefined) =>
    JSON.stringify(layout ?? {}, null, 2)

  //A valid layout must resolve to a plain object, not a primitive, array, or null
  const isObject = (value: unknown): value is unknown =>
    typeof value === "object" && value !== null && !Array.isArray(value)

  //Parses text into a layout, or null if it isn't valid JSON resolving to an object
  const parseLayout = (text: string): RADashboardLayout | null => {
    try {
      const parsed = JSON.parse(text)
      return isObject(parsed) ? (parsed as RADashboardLayout) : null
    } catch {
      return null
    }
  }

  //Repulls whenever props.layout changes (from another editor tab)
  React.useEffect(() => {
    setText((current) => {
      try {
        if (JSON.stringify(JSON.parse(current)) === JSON.stringify(props.layout ?? {})) {
          return current
        }
      } catch {
        // current text isn't valid JSON; fall through and reset from layout
      }
      return layoutToText(props.layout)
    })
    setIsValid(true)
  }, [props.layout])

  const onTextChange = (value: string) => {
    setText(value)

    const layout = parseLayout(value)
    if (layout) {
      props.setLayout(layout)
    }
    setIsValid(layout !== null)
  }

  return (
    <div style={{display: 'flex', marginTop: 24}}>
      <Box sx={{flex: 4}}>
        <TextField
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          error={!isValid}
          helperText={isValid ? undefined : "Invalid JSON. Changes can't be applied until this is valid."}
          multiline
          fullWidth
          spellCheck={false}
          InputProps={{ readOnly: props.isLocked}}
          inputProps={{ wrap: 'off' }}
          sx={{
            height: '100%',
            '& .MuiInputBase-root': {
              height: '100%',
              alignItems: 'flex-start',
              backgroundColor: theme.inputBackground,
              color: theme.textPrimary,
              fontFamily: 'monospace',
              fontSize: 14,
            },
          }}
        />
      </Box>
      <DashboardKeyStringsInfo dashboardType={props.dashboardType} />
    </div>
  );
}

export default DashboardTextEditor
